/**
 * NovaCard — the standard "Nova noticed" suggestion pattern.
 *
 * Spec: see ORBIT_DESIGN.md §7.5.
 *
 * Anatomy:
 *   ┌──────────────────────────────────────────┐
 *   │  ╭─╮  NOVA NOTICED                       │
 *   │  │N│  <message>                          │
 *   │  ╰─╯  ┌────────────┐  ┌────────┐         │
 *   │        │ Yes, do it │  │ Not now│         │
 *   │        └────────────┘  └────────┘         │
 *   └──────────────────────────────────────────┘
 *
 * Discipline:
 *   - Always two actions. Never one. Never dismiss-only.
 *   - Primary action label starts with a verb ("Yes, draft it" /
 *     "Yes, schedule it"). Secondary is always "Not now".
 *   - Once dismissed, this card does not reappear that day. The
 *     parent owns that gate.
 */

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
// #438 — the character render inside every "Nova noticed" card now
// comes from the 2026 kit. `NovaState` type stays imported from the
// old file so downstream consumers (novaSuggestion, useNovaSuggestions)
// don't need to change; mapNovaState translates it on render.
import { type NovaState } from "./Nova";
import { NovaExpression } from "@/components/nova/kit";
import { mapNovaState } from "@/components/nova/kit/novaStateMap";

export interface NovaCardProps {
  /** What Nova spotted, in her voice. Concrete, never generic. */
  message: string;

  /** Primary CTA. Label should start with a verb. */
  primaryAction: {
    label: string;
    onClick: () => void;
  };

  /** Optional secondary action. Defaults to `{ label: "Not now", onClick: noop }`. */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };

  /** Eyebrow label. Override only if you need a more specific framing. */
  label?: string;

  /** Nova's expression. Default: `has-suggestion`. */
  novaState?: NovaState;

  /** Extra wrapper classes. */
  className?: string;
}

export function NovaCard({
  message,
  primaryAction,
  secondaryAction,
  label = "Nova noticed",
  novaState = "has-suggestion",
  className,
}: NovaCardProps) {
  const secondary = secondaryAction ?? {
    label: "Not now",
    onClick: () => {},
  };

  return (
    <div
      className={cn(
        "rounded-2xl bg-card border border-border p-4",
        "animate-fade-in",
        className,
      )}
      role="region"
      aria-label="Nova suggestion"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0">
          <NovaExpression id={mapNovaState(novaState)} size={64} />
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-2 pt-1">
          <span className="label-caps text-accent">{label}</span>
          <p className="text-sm text-foreground leading-snug">{message}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              onClick={primaryAction.onClick}
              className="rounded-full px-4"
            >
              {primaryAction.label}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={secondary.onClick}
              className="rounded-full px-4 hover:bg-secondary"
            >
              {secondary.label}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NovaCard;
